import { DOCUMENT, Location, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Component, inject, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '@app/core';
import { AppRoutes } from '@app/core/routing';
import { BRAND, BrandThemeService } from '@app/core/brand';
import { SITE_CONTEXT, SiteContext } from '@app/core/site-context';
import {
  AuthService,
  Campaign,
  CampaignState,
  Charity,
  CharityState,
  Fundraiser,
  FundraiserState,
  GetFeatureFlags,
  GivebriteState,
  Logout,
  UserState,
  PosthogService,
  TeamState,
  Team,
} from '@app/state';
import { AuthState } from '@app/state/user/store/auth.state';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { CookieService } from 'ngx-cookie';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '@env';
import { CampaignAppService } from '@app/campaign-app.service';
import { cookieConsentConfig } from '@app/core/cookie-consent/cookie-config';

@UntilDestroy()
@Component({
  selector: 'root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly siteContext = inject(SITE_CONTEXT);
  private readonly brandTheme = inject(BrandThemeService);
  private readonly brand = inject(BRAND);
  private readonly document = inject(DOCUMENT);
  title = 'givebrite';
  loading$: Observable<boolean> = this.store.select(GivebriteState.loading);
  signingOut$: Observable<boolean> = this.store.select(AuthState.signingOut);
  campaignLoading$: Observable<boolean> = this.store.select(CampaignState.loading);
  fundraiserLoading$: Observable<boolean> = this.store.select(FundraiserState.loading);
  teamLoading$: Observable<boolean> = this.store.select(TeamState.loading);
  charityLoading$: Observable<boolean> = this.store.select(CharityState.loading);
  charityFromState$: Observable<any> = this.store.select(CharityState.getCharity);

  campaign$: Observable<Campaign> = this.store.select(CampaignState.getCampaign);
  fundraiser$: Observable<Fundraiser> = this.store.select(FundraiserState.fundraiser);
  team$: Observable<Team> = this.store.select(TeamState.campaignTeam);
  charity$: Observable<Charity> = this.campaignAppService.charity$;

  loading: boolean = true;
  isBrowser: boolean;
  isServer: boolean = false;
  env = environment;
  isLoaded: boolean = true;
  currentRoutePath = '';

  constructor(
    private apiService: ApiService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private cookies: CookieService,
    private campaignAppService: CampaignAppService,
    @Inject(PLATFORM_ID) platformId: Object,
    private location: Location,
    private auth: AuthService,
    private posthogService: PosthogService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.isServer = isPlatformServer(platformId);

    this.apiService.endpoints = environment?.endpoints;

    // Applied during SSR so the tokens are written onto :root and serialised
    // into the rendered HTML — see BrandThemeService for why this must run
    // through the injected Document rather than after hydration.
    this.brandTheme.apply(this.brand);
    if (this.siteContext.app === 'charity') {
      this.document.documentElement.classList.add('gb-charity-brand');
    }
  }

  ngOnInit() {
    // Load consumer feature flags on both server (SSR) and browser.
    // TransferState in FeatureFlagService ensures the server-fetched flags
    // are rehydrated on the client without a redundant HTTP call.
    this.store.dispatch(new GetFeatureFlags());

    if (this.isBrowser) {
      // If NGXS has a persisted user but the JWT cookie is gone, the session is
      // invalid. Clear state immediately so the user is treated as logged out.
      const hasUser = !!this.store.selectSnapshot(UserState.user);
      if (hasUser && !this.auth.jwt) {
        this.store.dispatch(new Logout(true));
      }

      this.currentRoutePath = this.location.path();

      this.loading$.pipe(untilDestroyed(this), debounceTime(2000)).subscribe((loading) => {
        this.loading = loading;
      });

      this.posthogService.capturePageView(window.location.href);

      if (this.siteContext.app === 'platform') {
        import('vanilla-cookieconsent').then((cookieConsent) => cookieConsent.run(cookieConsentConfig));
      }

      this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
        const jwt = params['jwt'];
        const reference = params['reference'];

        if (jwt) {
          if (reference !== 'undefined' && reference === 'dashboard')
            if (!this.cookies.get('jwt') || this.cookies.get('jwt') !== jwt) {
              this.cookies.put('jwt', jwt, {
                domain: this.computeCookieDomain(this.siteContext),
              });

              window.location.reload();
            }

          this.router.navigate([], {
            queryParams: { jwt: null, reference: null },
            queryParamsHandling: 'merge',
          });
        }
      });
    }
  }

  private computeCookieDomain(ctx: SiteContext): string {
    if (ctx.app === 'platform' || (ctx.app === 'charity' && !ctx.charity?.customDomain)) {
      return '.givebrite.com';
    }
    if (ctx.app === 'charity' && ctx.charity?.customDomain) {
      return '.' + ctx.host;
    }
    return ctx.host;
  }

  fundraise() {
    this.router.navigate([AppRoutes.create.root()]);
  }
}
